import * as React from 'react';

import { cn, ContentCard, ContentSection } from '@rematter/ui-web';

import { useSupplierAccount } from './hooks/useSupplierAccount';
import { PurchaseOrderTabSupplierInfoLocationForm } from './PurchaseOrderTabSupplierInfoLocationForm';
import { PurchaseOrderTabSupplierSectionContacts } from './PurchaseOrderTabSupplierSectionContacts';
import { PurchaseOrderTabSupplierSectionCustomAttributes } from './PurchaseOrderTabSupplierSectionCustomAttributes';

import { LabeledText } from '~/modules/trade/_components/salesOrders/LabeledText';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { PurchaseOrderRecord } from '~/modules/trade/types/purchaseOrder';

type Props = {
  purchaseOrder?: PurchaseOrderRecord | null;
  onRefetch?: () => void;
  disabled?: boolean;
  variant?: 'default' | 'embedded';
};

export const PurchaseOrderTabSupplier: React.FC<Props> = ({
  purchaseOrder,
  onRefetch,
  disabled = false,
  variant = 'default',
}) => {
  const t = useTranslations();

  const { selectedAccount } = useSupplierAccount(purchaseOrder?.account?.id);

  const afterSubmit = React.useCallback(async () => {
    if (onRefetch) {
      onRefetch();
    }
  }, [onRefetch]);

  if (!purchaseOrder) {
    return (
      <div className="p-6">
        <div className="rounded-lg bg-white p-6">
          <div className="py-8 text-center">
            <h3 className="mb-2 text-lg font-semibold text-neutral">
              {t.purchaseOrderDetail.supplier.loading}
            </h3>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        '@PurchaseOrderTabSupplier flex flex-col gap-5',
        variant !== 'embedded' && 'p-5',
      )}
    >
      <ContentCard
        className={cn(variant === 'embedded' && 'rounded-none border-none')}
      >
        <div className="mb-6 gap-[32px] rounded-lg px-8 pt-6">
          <ContentSection
            variant="panel"
            title={t.purchaseOrderDetail.supplier.sections.contractInfo}
          >
            {selectedAccount && (
              <PurchaseOrderTabSupplierInfoLocationForm
                purchaseOrder={purchaseOrder}
                afterSubmit={afterSubmit}
                disabled={disabled}
                selectedAccount={selectedAccount}
              />
            )}
          </ContentSection>

          <ContentSection
            variant="panel"
            title={t.purchaseOrderDetail.supplier.sections.profile}
          >
            <div className="mt-4 grid grid-cols-3 gap-4">
              <LabeledText
                label={t.purchaseOrderDetail.supplier.profile.companyType}
                text={t.shared.supplier}
              />
              <LabeledText
                label={t.purchaseOrderDetail.supplier.profile.companyName}
                text={purchaseOrder.account?.name}
              />
              <LabeledText
                label={t.purchaseOrderDetail.supplier.profile.contactName}
                text={purchaseOrder.contact?.fullName}
              />
              <LabeledText
                label={t.purchaseOrderDetail.supplier.profile.payeeName}
                text={t.shared.notAvailable}
              />
              <LabeledText
                label={t.purchaseOrderDetail.supplier.profile.customCompanyId}
                text={t.shared.notAvailable}
              />
              <LabeledText
                label={
                  t.purchaseOrderDetail.supplier.profile.businessLicenseNumber
                }
                text={t.shared.notAvailable}
              />
            </div>
          </ContentSection>

          <PurchaseOrderTabSupplierSectionCustomAttributes
            purchaseOrder={purchaseOrder}
          />

          {selectedAccount && (
            <PurchaseOrderTabSupplierSectionContacts
              selectedAccount={selectedAccount}
            />
          )}

          <ContentSection
            variant="panel"
            title={t.purchaseOrderDetail.supplier.sections.pricingPreferences}
          >
            <div className="mt-4 grid grid-cols-3 gap-4">
              <LabeledText
                label={t.purchaseOrderDetail.supplier.pricing.priceListGroup}
                text={t.shared.notAvailable}
              />
              <LabeledText
                label={t.purchaseOrderDetail.supplier.pricing.inboundTaxRate}
                text={t.shared.notAvailable}
              />
              <LabeledText
                label={t.purchaseOrderDetail.supplier.pricing.outboundTaxRate}
                text={t.shared.notAvailable}
              />
            </div>
          </ContentSection>

          <ContentSection
            variant="panel"
            title={t.purchaseOrderDetail.supplier.sections.paymentPreferences}
          >
            <div className="mt-4 grid grid-cols-3 gap-4">
              <LabeledText
                label={
                  t.purchaseOrderDetail.supplier.pricing.defaultPaymentType
                }
                text={t.shared.notAvailable}
              />
              <LabeledText
                label={
                  t.purchaseOrderDetail.supplier.pricing.defaultPaymentSchedule
                }
                text={purchaseOrder.paymentTerm}
              />
            </div>
          </ContentSection>
        </div>
      </ContentCard>
    </div>
  );
};
